import { useState, useEffect, useCallback, useRef } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { ChatParticipant, ChatRealtimeSubscription } from '../types';
import { getChatParticipants, getAdminChatParticipants } from '../services/chatService';

// Retry configuration for WebSocket connections
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

/**
 * Hook for getting chat participants (users who have sent messages to current user)
 * Used by admin to see all users they can chat with
 */
export interface UseChatParticipantsReturn {
  participants: ChatParticipant[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useChatParticipants = (): UseChatParticipantsReturn => {
  const { user, isAdmin } = usePermissions();
  const [participants, setParticipants] = useState<ChatParticipant[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionRef = useRef<ChatRealtimeSubscription | null>(null);
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isUnmountedRef = useRef<boolean>(false);

  const fetchParticipants = useCallback(async () => {
    if (!user?.id) {
      return;
    }


    setLoading(true);
    setError(null);

    try {
      // Use admin-specific function for admin users to see all conversations
      const data = isAdmin
        ? await getAdminChatParticipants(user.id)
        : await getChatParticipants(user.id);

      setParticipants(data);
    } catch (err) {
      const error = err as Error;
      console.error('Error fetching chat participants:', error);
      setError(error);
      toast.error('Failed to load chat participants');
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdmin]);

  // Handle real-time updates when messages change
  const handleMessageUpdate = useCallback(() => {
    // Refresh participants list when messages change
    fetchParticipants();
  }, [fetchParticipants]);

  // Calculate retry delay with exponential backoff
  const calculateRetryDelay = useCallback((retryCount: number): number => {
    const delay = RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, retryCount);
    return Math.min(delay, RETRY_CONFIG.maxDelay);
  }, []);

  // Setup WebSocket subscription with retry logic
  const setupSubscription = useCallback(() => {
    if (!user?.id || isUnmountedRef.current) {
      return;
    }

    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.cleanup();
      subscriptionRef.current = null;
    }

    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    try {
      const channel = supabase
        .channel(`participants_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `receiver_user_id.eq.${user.id}`,
          },
          () => {
            handleMessageUpdate();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'messages',
            filter: `or(receiver_user_id.eq.${user.id},sender_user_id.eq.${user.id})`,
          },
          () => {
            handleMessageUpdate();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `or(receiver_user_id.eq.${user.id},sender_user_id.eq.${user.id})`,
          },
          () => {
            handleMessageUpdate();
          }
        )
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            // Reset retry count on successful connection
            retryCountRef.current = 0;
            console.log('✅ Chat participants subscription established');
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            // Handle connection errors with retry logic
            if (!isUnmountedRef.current && retryCountRef.current < RETRY_CONFIG.maxRetries) {
              const delay = calculateRetryDelay(retryCountRef.current);
              console.warn(`⚠️ Chat subscription failed (attempt ${retryCountRef.current + 1}/${RETRY_CONFIG.maxRetries}), retrying in ${delay}ms...`);

              retryCountRef.current++;
              retryTimeoutRef.current = setTimeout(() => {
                if (!isUnmountedRef.current) {
                  setupSubscription();
                }
              }, delay);
            } else if (retryCountRef.current >= RETRY_CONFIG.maxRetries) {
              console.error('❌ Chat subscription failed after maximum retries');
              setError(new Error('Failed to establish chat connection after multiple attempts'));
            }
          }
        });

      subscriptionRef.current = {
        channel: channel.topic,
        subscription: channel,
        cleanup: () => {
          supabase.removeChannel(channel);
        },
      };
    } catch (err) {
      console.error('Error setting up participants subscription:', err);
      if (!isUnmountedRef.current && retryCountRef.current < RETRY_CONFIG.maxRetries) {
        const delay = calculateRetryDelay(retryCountRef.current);
        retryCountRef.current++;
        retryTimeoutRef.current = setTimeout(() => {
          if (!isUnmountedRef.current) {
            setupSubscription();
          }
        }, delay);
      }
    }
  }, [user?.id, handleMessageUpdate, calculateRetryDelay]);

  // Setup real-time subscription to refresh participants when new messages arrive
  // Use a different channel name to avoid conflicts with useUnreadChatCount
  useEffect(() => {
    isUnmountedRef.current = false;

    if (!user?.id) {
      return;
    }

    // Reset retry count when user changes
    retryCountRef.current = 0;

    // Setup subscription with retry logic
    setupSubscription();

    // Cleanup on unmount or dependency change
    return () => {
      isUnmountedRef.current = true;

      // Clear retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      // Clean up subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.cleanup();
        subscriptionRef.current = null;
      }
    };
  }, [user?.id, setupSubscription]);

  // Load participants on mount and when user changes
  useEffect(() => {
    fetchParticipants();
  }, [fetchParticipants]);

  // Refresh participants when app regains focus (fallback for realtime issues)
  useEffect(() => {
    const handleFocus = () => {
      if (user?.id) {
        fetchParticipants();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchParticipants, user?.id]);



  return {
    participants,
    loading,
    error,
    refetch: fetchParticipants,
  };
};
